import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { UserGroup } from '../../shared/models';
import { Message, MessageActionType, MessageType, AppComponentName, MessageSearchCriteria } from '../../shared/models/Messages/message.model';
import { AppMessageService } from '../../shared/services/message.service';
import { UserService } from '../../shared/services/user.service';
import { DateRanges } from '@models/ranges';

@Component({
  standalone: false,
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss']
})

export class MessagesComponent implements OnInit {

  modalDragOn: boolean = false;

  public username = 'unknown';
  public messagesLoaded = false;
  public associatedUserGroups: UserGroup[] = [];

  public allMessages: Message[] = [];
  public auditMessagesDisplayed: Message[] = [];
  public auditMsgTypesSelected: boolean[] = [ true, true, true ];
  public auditAppFuncsSelectAll = false;
  public auditAppFuncsSelected: boolean[] = [ true, true, true, true, true, true, true, true, true ];
  public alertMessagesDisplayed: Message[] = [];
  public alertMsgTypesSelected: boolean[] = [ true, true, true ];
  public alertAppFuncsSelectAll = false;
  public alertShowAcknowleged = false;
  public alertAppFuncsSelected: boolean[] = [ true, true, true, true, true, true, true, true, true ];

  public auditCols = [
    {field: 'createdDate', header: 'Date', width: '13%' },
    {field: 'userName', header: 'User', width: '10%' },
    {field: 'appComponentName', header: 'Tab', width: '10%' },
    {field: 'actionType', header: 'Type', width: '7%' },
    {field: 'appComponentItem', header: 'Item', width: '10%' },
    {field: 'details', header: 'Changes', width: '35%' },
    {field: 'userNotes', header: 'Notes', width: '15%' }
  ];

  public alertCols = [
    {field: 'createdDate', header: 'Date', width: '13%' },
    {field: 'appComponentName', header: 'Tab', width: '10%' },
    {field: 'actionType', header: 'Type', width: '7%' },
    {field: 'appComponentItem', header: 'Item Name', width: '12%' },
    {field: 'details', header: 'Details', width: '46%' },
    {field: 'count', header: '#', width: '5%' },
    {field: 'acknowledged', header: 'Ack\'ed', width: '7%' }
  ];

  alwaysShowCalendars: boolean;
  ranges = DateRanges;
  invalidDates: moment.Moment[] = [];

  public selectedRange: any; // = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
  public selectedDateRange: any; // = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
  public userNameTimer: any;

  constructor(public toastr: ToastrService, public _userService: UserService, public _appMessageService: AppMessageService) { }

  isInvalidDate = (m: moment.Moment) => this.invalidDates.some(d => d.isSame(m, 'day'));


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {

        this.username = results['value'];
        if (this.username != null && this.username != '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.selectedDateRange = { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
    this.datesUpdated();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  acknowledgeAlert(message: Message) {
    message.acknowledged = !message.acknowledged;
    const messageInfo = this._appMessageService.mapMessage2MessageInfo(message);

    this._appMessageService.updateMessage(messageInfo).subscribe(
      results => {
        if (results != null) {
          this.toastr.success('Message acknowledged');
          this.alertFilterChanged();
        } else {
          this.toastr.warning('Unable to update message');
        }
        return;
      },
      err => {
        this.toastr.error('Unable to update message');
      });
  }


  datesUpdated(): void {
    if (this.selectedDateRange == null ||
        this.selectedDateRange.startDate == null ||
        this.selectedDateRange.endDate == null) {
      return;
    }

    const messageSearchCriteria = {
      username: this.username,
      startDate: this.selectedDateRange.startDate.local().toDate(),
      endDate: this.selectedDateRange.endDate.local().toDate()
    };

    this.getMessages(messageSearchCriteria);
  }


  getMessages(params: MessageSearchCriteria): void {
    this.dateUTC(params);
    this._appMessageService.getAppMessages(params).subscribe(
      results => {
        if (results != null) {
          this.allMessages = this._appMessageService.mapMessageInfos2Messages(results);
          this.auditFilterChanged();
          this.alertFilterChanged();
        } else {
          this.toastr.warning('No messages found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving messages.');
      });
  }


  dateUTC(params: MessageSearchCriteria) {
    params.endDate = moment(params.endDate).add(-(moment().utcOffset()), 'm').toDate();
    params.startDate = moment(params.startDate).add(-(moment().utcOffset()), 'm').toDate();
  }


  auditFilterChanged() {
    this.auditMessagesDisplayed = [];
    const selectedMessageTypes: Message[] = [];
    const selectedAppFunctions: Message[] = [];

    if (this.auditMsgTypesSelected[0]) {
      const msgs = this.allMessages.filter(m => m.actionType == MessageActionType.New);
      if (msgs != undefined) {
        selectedMessageTypes.push(...msgs);
      }
    }

    if (this.auditMsgTypesSelected[1]) {
      const msgs = this.allMessages.filter(m => m.actionType == MessageActionType.Edited);
      if (msgs != undefined) {
        selectedMessageTypes.push(...msgs);
      }
    }

    if (this.auditMsgTypesSelected[2]) {
      const msgs = this.allMessages.filter(m => m.actionType == MessageActionType.Deleted);
      if (msgs != undefined) {
        selectedMessageTypes.push(...msgs);
      }
    }

    const msgs = this.filterAppFunctions(MessageType.Audit, this.allMessages);
    if (msgs != undefined) {
      selectedAppFunctions.push(...msgs);
    }

    selectedMessageTypes.forEach(t => {
      selectedAppFunctions.forEach(a => {
        if (t.id == a.id) {
          this.auditMessagesDisplayed.push(t);
        }
      });
    });

    this.sortMessagesByID(this.auditMessagesDisplayed);
  }


  alertFilterChanged() {
    this.alertMessagesDisplayed = [];
    const selectedMessageTypes: Message[] = [];
    const selectedAppFunctions: Message[] = [];

    if (this.alertMsgTypesSelected[0]) {
      const msgs = this.allMessages.filter(m => m.actionType == MessageActionType.Message && (!m.acknowledged || m.acknowledged == this.alertShowAcknowleged));
      if (msgs != undefined) {
        selectedMessageTypes.push(...msgs);
      }
    }

    if (this.alertMsgTypesSelected[1]) {
      const msgs = this.allMessages.filter(m => m.actionType == MessageActionType.Email && (!m.acknowledged || m.acknowledged == this.alertShowAcknowleged));
      if (msgs != undefined) {
        selectedMessageTypes.push(...msgs);
      }
    }

    const msgs = this.filterAppFunctions(MessageType.Alert, this.allMessages);
    if (msgs != undefined) {
      selectedAppFunctions.push(...msgs);
    }

    selectedMessageTypes.forEach(t => {
      selectedAppFunctions.forEach(a => {
        if (t.id == a.id) {
          this.alertMessagesDisplayed.push(t);
        }
      });
    });

    this.sortMessagesByID(this.alertMessagesDisplayed);
    this.removeBreakFromAlertDetails(this.alertMessagesDisplayed);
  }


  sortMessagesByID(messages: Message[]) {
    messages.sort((a, b) => {
      if (b.id > a.id) {
        return 1;
      }
      if (b.id < a.id) {
        return -1;
      }
    });
  }


  removeBreakFromAlertDetails(messages: Message[]) {
    if (messages.length > 0) {
      messages.forEach(x => {
        x.details = x.details.replace(/<br>/g, '');
        x.details = x.details.replace(/ lt /g, ' < ');
        x.details = x.details.replace(/ lte /g, ' <= ');
        x.details = x.details.replace(/ gt /g, ' > ');
        x.details = x.details.replace(/ gte /g, ' >= ');
        x.details = x.details.replace(/ eq /g, ' = ');
        x.details = x.details.replace(/ neq /g, ' != ');
      });
    }
  }


  filterAppFunctions(msgType: MessageType, messages: Message[]): Message[] {
    const returnMessages: Message[] = [];
    let appFunctionSelection: boolean[] = [];

    if (msgType == MessageType.Alert) {
      appFunctionSelection = this.alertAppFuncsSelected;
    }
    if (msgType == MessageType.Audit) {
      appFunctionSelection = this.auditAppFuncsSelected;
    }

    for (let i = 0; i < appFunctionSelection.length; i++) {
      if (appFunctionSelection[i]) {
        const msgs = messages.filter(m => m.appComponentName == Object.values(AppComponentName)[i] && (!m.acknowledged || m.acknowledged == this.alertShowAcknowleged));
        if (msgs != undefined) {
          returnMessages.push(...msgs);
        }
      }
    }

    return returnMessages;
  }


  appFunctionsSelectAll(msgType: string) {
    if (msgType == 'audit') {
      this.auditAppFuncsSelected.forEach(m => m = this.auditAppFuncsSelectAll);
    }
    if (msgType == 'alert') {
      this.alertAppFuncsSelected.forEach(m => m = this.alertAppFuncsSelectAll);
    }
  }


  setRangeSelected(selectedRange: any, mode: string) {
    switch (mode) {
    case 'Common':
      this.selectedRange = selectedRange;
      break;

    default:
      break;
    }
  }


}
